class MaurtenScrollRotator extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.frameRequest = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchCurrentX = 0;
    this.touchCurrentY = 0;
    this.isTrackingTouch = false;
    this.transitionCleanupTimer = 0;
    this.wheelReleaseTimer = 0;
    this.isWheelLocked = false;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-maurten-scroll-rotator-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-maurten-scroll-rotator-dot]'));
    this.viewport = this.querySelector('.maurten-scroll-rotator__viewport');

    if (!this.slides.length || !this.viewport) return;

    this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick));
    this.viewport.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.viewport.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.viewport.addEventListener('touchend', this.handleTouchEnd);
    this.viewport.addEventListener('touchcancel', this.handleTouchCancel);
    this.viewport.addEventListener('wheel', this.handleWheel, { passive: false });
    window.addEventListener('resize', this.requestViewportUpdate);
    window.visualViewport?.addEventListener('resize', this.requestViewportUpdate);
    window.visualViewport?.addEventListener('scroll', this.requestViewportUpdate);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.mobileQuery.addEventListener('change', this.handleModeChange);

    this.setActiveSlide(0, { animate: false });
    this.updateMobileViewportHeight();
  }

  disconnectedCallback() {
    this.dots?.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));
    this.viewport?.removeEventListener('touchstart', this.handleTouchStart);
    this.viewport?.removeEventListener('touchmove', this.handleTouchMove);
    this.viewport?.removeEventListener('touchend', this.handleTouchEnd);
    this.viewport?.removeEventListener('touchcancel', this.handleTouchCancel);
    this.viewport?.removeEventListener('wheel', this.handleWheel);
    window.removeEventListener('resize', this.requestViewportUpdate);
    window.visualViewport?.removeEventListener('resize', this.requestViewportUpdate);
    window.visualViewport?.removeEventListener('scroll', this.requestViewportUpdate);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.mobileQuery.removeEventListener('change', this.handleModeChange);

    if (this.frameRequest) {
      cancelAnimationFrame(this.frameRequest);
    }

    if (this.wheelReleaseTimer) {
      clearTimeout(this.wheelReleaseTimer);
    }

    if (this.transitionCleanupTimer) {
      clearTimeout(this.transitionCleanupTimer);
    }
  }

  handleDotClick = (event) => {
    const dot = event.currentTarget;
    const index = Number(dot?.dataset?.slideIndex);

    if (Number.isNaN(index)) return;

    if (index === this.activeIndex) return;

    this.setActiveSlide(index, {
      direction: index > this.activeIndex ? 'next' : 'previous',
    });
  };

  handleModeChange = () => {
    this.updateMobileViewportHeight();
    this.setActiveSlide(this.activeIndex, { animate: false });
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.pauseAllVideos();
    } else {
      this.playActiveVideo();
    }
  };

  handleTouchStart = (event) => {
    if (!this.mobileQuery.matches || this.slides.length <= 1 || event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.isTrackingTouch = true;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchCurrentX = touch.clientX;
    this.touchCurrentY = touch.clientY;
  };

  handleTouchMove = (event) => {
    if (!this.isTrackingTouch || event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.touchCurrentX = touch.clientX;
    this.touchCurrentY = touch.clientY;

    const deltaX = this.touchCurrentX - this.touchStartX;
    const deltaY = this.touchCurrentY - this.touchStartY;
    const isVerticalGesture = Math.abs(deltaY) >= 16 && Math.abs(deltaY) > Math.abs(deltaX) * 1.2;

    if (isVerticalGesture) {
      event.preventDefault();
    }
  };

  handleTouchEnd = () => {
    if (!this.isTrackingTouch) return;

    const deltaX = this.touchCurrentX - this.touchStartX;
    const deltaY = this.touchCurrentY - this.touchStartY;
    const isHorizontalSwipe = Math.abs(deltaX) >= 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4;
    const isVerticalSwipe = Math.abs(deltaY) >= 48 && Math.abs(deltaY) > Math.abs(deltaX) * 1.2;

    this.handleTouchCancel();

    if (isVerticalSwipe) {
      const direction = deltaY < 0 ? 'next' : 'previous';
      this.setWrappedSlide(this.activeIndex + (direction === 'next' ? 1 : -1), direction);
      return;
    }

    if (isHorizontalSwipe) {
      const direction = deltaX < 0 ? 'next' : 'previous';
      this.setWrappedSlide(this.activeIndex + (direction === 'next' ? 1 : -1), direction);
    }
  };

  handleTouchCancel = () => {
    this.isTrackingTouch = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchCurrentX = 0;
    this.touchCurrentY = 0;
  };

  handleWheel = (event) => {
    if (this.slides.length <= 1 || Math.abs(event.deltaY) < 20) return;

    const direction = event.deltaY > 0 ? 1 : -1;

    event.preventDefault();

    if (this.isWheelLocked) return;

    this.setWrappedSlide(this.activeIndex + direction, direction > 0 ? 'next' : 'previous');
    this.isWheelLocked = true;

    if (this.wheelReleaseTimer) {
      clearTimeout(this.wheelReleaseTimer);
    }

    this.wheelReleaseTimer = setTimeout(() => {
      this.isWheelLocked = false;
      this.wheelReleaseTimer = 0;
    }, 520);
  };

  requestViewportUpdate = () => {
    if (this.frameRequest) return;

    this.frameRequest = requestAnimationFrame(() => {
      this.frameRequest = 0;
      this.updateMobileViewportHeight();
    });
  };

  updateMobileViewportHeight() {
    if (!this.mobileQuery.matches) {
      this.style.removeProperty('--maurten-scroll-rotator-mobile-viewport-height');
      return;
    }

    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    this.style.setProperty('--maurten-scroll-rotator-mobile-viewport-height', `${viewportHeight}px`);
  }

  setActiveSlide(index, options = {}) {
    const nextIndex = Math.min(Math.max(index, 0), this.slides.length - 1);
    const previousIndex = this.activeIndex;
    const isChangingSlide = nextIndex !== previousIndex;
    const shouldAnimate = options.animate !== false && isChangingSlide && !this.reducedMotionQuery.matches;
    const transitionDirection =
      options.direction || (nextIndex > previousIndex || previousIndex === this.slides.length - 1 ? 'next' : 'previous');

    if (this.transitionCleanupTimer) {
      clearTimeout(this.transitionCleanupTimer);
      this.transitionCleanupTimer = 0;
    }

    this.slides.forEach((slide) => {
      slide.classList.remove('is-exiting');
    });

    if (shouldAnimate) {
      this.dataset.transitionDirection = transitionDirection;
      void this.offsetHeight;
    } else {
      delete this.dataset.transitionDirection;
    }

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === nextIndex;
      const isExiting = shouldAnimate && slideIndex === previousIndex;
      slide.classList.toggle('is-active', isActive);
      slide.classList.toggle('is-exiting', isExiting);
      slide.setAttribute('aria-hidden', String(!isActive));
      this.updateVideos(slide, isActive);
    });

    this.dots?.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-selected', String(dotIndex === nextIndex));
    });

    this.activeIndex = nextIndex;

    if (shouldAnimate) {
      this.transitionCleanupTimer = setTimeout(() => {
        this.slides[previousIndex]?.classList.remove('is-exiting');
        delete this.dataset.transitionDirection;
        this.transitionCleanupTimer = 0;
      }, 560);
    }
  }

  setWrappedSlide(index, direction) {
    this.setActiveSlide(this.getWrappedSlideIndex(index), { direction });
  }

  getWrappedSlideIndex(index) {
    return ((index % this.slides.length) + this.slides.length) % this.slides.length;
  }

  updateVideos(slide, isActive) {
    const videos = Array.from(slide.querySelectorAll('video'));

    videos.forEach((video) => {
      const isVisibleMedia = getComputedStyle(video).display !== 'none';

      if (isActive && isVisibleMedia && !document.hidden) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  playActiveVideo() {
    this.updateVideos(this.slides[this.activeIndex], true);
  }

  pauseAllVideos() {
    this.slides.forEach((slide) => {
      Array.from(slide.querySelectorAll('video')).forEach((video) => video.pause());
    });
  }
}

if (!customElements.get('maurten-scroll-rotator')) {
  customElements.define('maurten-scroll-rotator', MaurtenScrollRotator);
}
